import React, { useState, useEffect } from 'react';
import { Spinner, Alert } from 'reactstrap';
import locationService from 'service/locationService';

/**
 * DashboardEmbed Component
 * Reusable component for embedding AWS QuickSight dashboards
 * 
 * @param {string} dashboardName - Name of the dashboard to embed (required)
 * @param {string} title - Title for the iframe (default: 'Dashboard')
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
function DashboardEmbed({ 
  dashboardName = 'initial_dashboard',
  title = 'Dashboard',
  className = '',
  style = {}
}) {
  const [embedUrl, setEmbedUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchDashboardUrl = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await locationService.getDashboardEmbedUrl(dashboardName);
        
        if (isMounted) {
          setEmbedUrl(data.embedUrl);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching dashboard URL:', err);
          setError(err.message || 'Failed to load dashboard');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDashboardUrl();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [dashboardName]);

  // Loading state
  if (loading) {
    return (
      <div 
        className={`d-flex justify-content-center align-items-center ${className}`}
        style={{ 
          minHeight: '400px',
          ...style 
        }}
      >
        <div className="text-center">
          <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={className} style={style}>
        <Alert color="danger">
          <h4 className="alert-heading">
            <i className="ni ni-fat-remove mr-2"></i>
            Error Loading Dashboard
          </h4>
          <p className="mb-0">{error}</p>
          <hr />
          <p className="mb-0 small">
            Please try refreshing the page or contact support if the problem persists.
          </p>
        </Alert>
      </div>
    );
  }

  // Success state - Render iframe
  return (
    <div
      className={className}
      style={{
        position: "relative",
        width: "100%",
        paddingBottom: "50%", // Increased from 56.25% (16:9) to 75% (4:3) for more height
        height: 0,
        overflow: "hidden",
        borderRadius: "12px",
        ...style
      }}
    >
      <iframe
        src={embedUrl}
        title={title}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          border: "none",
        }}
        allowFullScreen
      />
    </div>
  );
}

export default DashboardEmbed;

